import * as React from 'react';
import { cn } from '../../lib/cn';

/**
 * StatTile — KPI card from the NV Design System `#cards` spec.
 *
 * Surfaces a single metric with a kicker label, bold tabular-numeral value,
 * and an optional delta line. Use `.glass-soft` (default) or `.glass-accent`
 * (accent variant) surfaces matching the design system stat tile pattern.
 *
 * Reference: docs/design-system/index.html #cards
 */
export interface StatTileProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Short uppercase descriptor — e.g. "Total Invoiced" */
  kicker: string;
  /** Primary metric — string or number, already formatted for display */
  value: string | number;
  /**
   * Optional delta line — e.g. "+12%" or "+2 this month". A `ReactNode` rather
   * than a bare string so callers can pass markup that controls its own line
   * wrapping (e.g. a multi-currency list whose amounts must each stay on one
   * line); a plain string still works unchanged. Inline content only — it
   * renders inside a `<p>`, so a block-level child would be invalid nesting.
   */
  delta?: React.ReactNode;
  /** Semantic colour of the delta line. Defaults to 'neutral'. */
  deltaIntent?: 'positive' | 'negative' | 'neutral';
  /** Surface variant. 'default' = glass-soft, 'accent' = glass-accent. */
  variant?: 'default' | 'accent';
  /**
   * Override the value line's default `text-[28px] font-bold`. For the rare
   * case where `value` is not more authoritative than `delta` — e.g. one
   * native currency picked arbitrarily to headline a multi-currency bucket
   * that has no single converted total — and the two need to read as equally
   * weighted rather than headline-vs-annotation.
   */
  valueClassName?: string;
  /** Override the delta line's default `text-[12px] font-medium`. See {@link valueClassName}. */
  deltaClassName?: string;
  /**
   * Optional follow-up action rendered under the delta line — e.g. a
   * "Review" button that takes the user to the items the metric counts.
   */
  action?: React.ReactNode;
}

export function StatTile({
  kicker,
  value,
  delta,
  deltaIntent = 'neutral',
  variant = 'default',
  className,
  valueClassName,
  deltaClassName,
  action,
  ...props
}: StatTileProps) {
  return (
    <div
      className={cn(
        variant === 'accent' ? 'glass-accent' : 'glass-soft',
        'rounded-2xl p-3 sm:p-5',
        className,
      )}
      data-testid="stat-tile"
      data-variant={variant}
      {...props}
    >
      <p className="stat-tile-kicker text-[11px] sm:text-[12px] uppercase tracking-[0.06em] sm:tracking-[0.08em] text-[color:var(--ink-muted)] font-semibold mb-1 sm:mb-1.5">
        {kicker}
      </p>
      <p
        className={cn(
          'stat-tile-value text-[22px] sm:text-[28px] font-bold num text-[color:var(--ink)]',
          valueClassName,
        )}
      >
        {value}
      </p>
      {delta !== undefined && delta !== null && (
        <p
          className={cn(
            'stat-tile-delta text-[11px] sm:text-[12px] font-medium mt-0.5',
            {
              'curr-pos': deltaIntent === 'positive',
              'curr-neg': deltaIntent === 'negative',
              'text-[color:var(--ink-muted)]': deltaIntent === 'neutral',
            },
            deltaClassName,
          )}
        >
          {delta}
        </p>
      )}
      {action !== undefined && (
        <div className="stat-tile-action mt-2">{action}</div>
      )}
    </div>
  );
}
